import React, { useState, useEffect } from 'react'
import Grid from '@mui/material/Grid'
import SelectField from '@/v2/components/common/SelectField'
import TextField from '@/v2/components/common/TextField'
import DateOfBirthInput from '@/v2/components/common/DateOfBirthInput'
import Typography from '@/v2/components/common/Typography'
import { RELATIONSHIP_OPTIONS } from '@/v2/constants/insurance'
import { genderOptions } from '../../utils'
import Divider from '@/v2/components/common/Divider'
import { useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Button from '@/v2/components/common/Button'
import { CancelOutlined, EditOutlined, SaveOutlined } from '@mui/icons-material'
import { useUpdateClaimInfo } from '@/v2/hooks/insurance/useClaims'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'

const Patient = ({
  values = {},
  error = {},
  insuranceId,
  claimId,
  patientId,
  viewOnly = false,
}) => {
  const theme = useTheme()
  const formatRelationshipOptions = (array = []) =>
    array.map(item => ({
      value: item,
      label: item.charAt(0).toUpperCase() + item.slice(1),
    }))
  const { patientInfo: patient } = values
  const [allowPatientEdit, setAllowPatientEdit] = useState(false)
  const [patientData, setPatientData] = useState(patient)
  const { updateClaim } = useUpdateClaimInfo(insuranceId, claimId, patientId)
  const { onOpenSnackbar } = useSnackbarContext()
  const handleSave = async () => {
    const res = await updateClaim({
      body: {
        insuranceId,
        patientInfoAttributes: {
          ...patientData,
        },
      },
    })
    if (!res.error) {
      setAllowPatientEdit(false)
      onOpenSnackbar('Patient information updated successfully', 'success')
    } else {
      onOpenSnackbar(res.error, 'error')
    }
  }

  const handleCancel = () => {
    setAllowPatientEdit(false)
    setPatientData(patient)
  }

  useEffect(() => {
    setPatientData(patient)
  }, [patient])

  return (
    <>
      <Grid item xs={12} sx={{ marginTop: '12px' }}>
        <Typography variant="h5" bold fontSize="16px">
          Patient Information
        </Typography>
        <Divider margin="5px 0px" />
      </Grid>

      <>
        {patient && (
          <>
            <Grid item xs={6} sm={6} md={3}>
              <TextField
                outlineColor={
                  allowPatientEdit
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main
                }
                fullWidth
                name="firstName"
                label="First Name"
                placeholder="First Name"
                value={patientData?.firstName || ''}
                error={error['patient.firstName']}
                onChange={value =>
                  setPatientData({ ...patientData, firstName: value })
                }
                disabled={!allowPatientEdit}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <TextField
                outlineColor={
                  allowPatientEdit
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main
                }
                fullWidth
                name="lastName"
                label="Last Name"
                placeholder="Last Name"
                value={patientData?.lastName || ''}
                error={error['patient.lastName']}
                onChange={value =>
                  setPatientData({ ...patientData, lastName: value })
                }
                disabled={!allowPatientEdit}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <DateOfBirthInput
                label="Date of birth"
                value={patientData?.dob || ''}
                error={error['patient.dob']}
                onChange={value =>
                  setPatientData({ ...patientData, dob: value })
                }
                disabled={!allowPatientEdit}
                helpText="Enter date as MM/DD/YYYY"
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <SelectField
                outlineColor={
                  allowPatientEdit
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main
                }
                fullWidth
                noMargin
                name="gender"
                label="Gender"
                placeholder="Choose an option"
                options={genderOptions}
                value={patientData?.gender || ''}
                error={error['patient.gender']}
                onChange={value =>
                  setPatientData({ ...patientData, gender: value })
                }
                disabled={!allowPatientEdit}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <SelectField
                outlineColor={
                  allowPatientEdit
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main
                }
                fullWidth
                noMargin
                name="relationship"
                label="Relationship"
                placeholder="Choose an option"
                options={formatRelationshipOptions(RELATIONSHIP_OPTIONS)}
                value={patientData?.relationship || ''}
                error={error['patient.relationship']}
                onChange={value =>
                  setPatientData({ ...patientData, relationship: value })
                }
                disabled={!allowPatientEdit}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <TextField
                outlineColor={
                  allowPatientEdit
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main
                }
                fullWidth
                name="memberId"
                label="Member ID"
                placeholder="E.g.: 123456789"
                value={patientData?.memberId || ''}
                error={error['patient.memberId']}
                onChange={value =>
                  setPatientData({ ...patientData, memberId: value })
                }
                disabled={!allowPatientEdit}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <TextField
                outlineColor={
                  allowPatientEdit
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main
                }
                fullWidth
                name="groupNumber"
                label="Group Number"
                placeholder="E.g.: 123456789"
                value={patientData?.groupNumber || ''}
                error={error['patient.groupNumber']}
                onChange={value =>
                  setPatientData({ ...patientData, groupNumber: value })
                }
                disabled={!allowPatientEdit}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <TextField
                outlineColor={
                  allowPatientEdit
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main
                }
                fullWidth
                name="address1"
                label="Address"
                value={patientData?.address1 || ''}
                onChange={value =>
                  setPatientData({ ...patientData, address1: value })
                }
                disabled={!allowPatientEdit}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <TextField
                outlineColor={
                  allowPatientEdit
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main
                }
                fullWidth
                name="address2"
                label="Address 2"
                value={patientData?.address2 || ''}
                onChange={value =>
                  setPatientData({ ...patientData, address2: value })
                }
                disabled={!allowPatientEdit}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <TextField
                outlineColor={
                  allowPatientEdit
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main
                }
                fullWidth
                name="zipcode"
                label="Postal Code"
                value={patientData?.zipcode || ''}
                onChange={value =>
                  setPatientData({ ...patientData, zipcode: value })
                }
                disabled={!allowPatientEdit}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <TextField
                outlineColor={
                  allowPatientEdit
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main
                }
                fullWidth
                name="city"
                label="City"
                value={patientData?.city || ''}
                onChange={value =>
                  setPatientData({ ...patientData, city: value })
                }
                disabled={!allowPatientEdit}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={3}>
              <TextField
                outlineColor={
                  allowPatientEdit
                    ? theme.palette.secondary.main
                    : theme.palette.primary.main
                }
                fullWidth
                name="state"
                label="State"
                value={patientData?.state || ''}
                onChange={value =>
                  setPatientData({ ...patientData, state: value })
                }
                disabled={!allowPatientEdit}
              />
            </Grid>
            {!viewOnly && (
              <Grid item xs={12}>
                <Box display="flex" flexDirection="row">
                  <Button
                    variant="text"
                    disableElevation
                    aria-label={allowPatientEdit ? 'Cancel' : 'Edit Patient'}
                    startIcon={
                      allowPatientEdit ? (
                        <CancelOutlined color="error" />
                      ) : (
                        <EditOutlined color="link" />
                      )
                    }
                    onClick={() => {
                      if (allowPatientEdit) {
                        handleCancel()
                      }
                      setAllowPatientEdit(!allowPatientEdit)
                    }}
                    sx={{
                      color: '#000',
                      marginBottom: '4px',
                      '&:hover': {
                        background: theme.palette.silver.dark,
                      },
                    }}
                  >
                    {allowPatientEdit ? 'Cancel' : 'Edit'}
                  </Button>
                  {allowPatientEdit && (
                    <Button
                      variant="text"
                      disableElevation
                      startIcon={<SaveOutlined color="link" />}
                      onClick={handleSave}
                      sx={{
                        color: '#000',
                        marginBottom: '4px',
                        '&:hover': {
                          background: theme.palette.silver.dark,
                        },
                        marginLeft: '10px',
                      }}
                    >
                      Save
                    </Button>
                  )}
                </Box>
              </Grid>
            )}
          </>
        )}
      </>
    </>
  )
}

export default Patient
